// src/components/agents/agent-builder.tsx
// REQUIRES: npm install reactflow

import React, { useCallback, useMemo, useState, useEffect } from "react";
import ReactFlow, {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useReactFlow,
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  Position,
  MarkerType,
  type Node,
  type Edge,
  type Connection,
  type NodeProps,
} from "reactflow";
import "reactflow/dist/style.css";

import {
  Save, Bot, AlertCircle, Wand2, Trash2, Check,
  ArrowLeft, MousePointerClick, Link, Monitor,
} from "lucide-react";

import { Agent, agentsApi } from "@/app/services/agents/agents-api";
import { Button } from "../kit";
import {
  WORKFLOW_BLOCKS, SETUP_TYPES, OUTPUT_TYPES, edgeLabelFor, type BlockType,
} from "./workflow-blocks";
import "@/styles/advanced-tier.css";
import "@/styles/workflows-page.css";
import { toast } from "sonner";

// Centralised model catalog — single source of truth for all model
// id/label/provider/availability data across the app.
import {
  MEASUREMENT_MODELS,
  MEASUREMENT_DEFAULT_MODELS,
  getModelFullLabel,
} from "@/app/constants/models";

type AgentBody = Omit<Agent, "id" | "username" | "is_active" | "created_at" | "updated_at">;

const MIN_BUILDER_WIDTH = 900;

// ─── Types ────────────────────────────────────────────────────────────────────

interface NodeData {
  blockType: BlockType;
  label: string;
  config: Record<string, any>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MODEL_OPTIONS = MEASUREMENT_MODELS.map((id) => ({ id, label: getModelFullLabel(id) }));

const PROMPT_STARTERS = [
  "How does {brand} rank compared to competitors in AI model responses?",
  "What are the key strengths and weaknesses of {brand}?",
  "How accurately do AI models describe {brand}'s pricing and features?",
  "What sentiment do AI models express toward {brand}?",
];

/**
 * `BLOCK_DEFS` is `WORKFLOW_BLOCKS` — one table, shared with the list view,
 * which previews each workflow as its graph. Two copies of it is one table and
 * one almost-table.
 */
const BLOCK_DEFS = WORKFLOW_BLOCKS;

/** The stored label is the display label; both are sentence case now. */
function blockLabel(type: BlockType): string {
  return BLOCK_DEFS[type].label;
}



// ─── Helpers ──────────────────────────────────────────────────────────────────

function getNodeSummary(data: NodeData): string {
  const c = data.config || {};
  switch (data.blockType) {
    case "brand_context":    return c.brand_name || "No brand set";
    case "analysis_prompt":  return c.prompt ? (c.prompt.length > 55 ? c.prompt.slice(0, 55) + "…" : c.prompt) : "No prompt set";
    case "ai_models":        return (c.models || []).join(", ") || "No models selected";
    case "send_email":       return c.email || "No email set";
    case "perception_score": return "Generates 0–100 score";
    case "download_report":  return "Exports JSON report";
    default: return "";
  }
}

const getEdgeLabel = edgeLabelFor;

function agentToFlow(agent: Agent): { nodes: Node<NodeData>[]; edges: Edge[] } {
  const nodes: Node<NodeData>[] = [
    { id: "brand_context",   type: "workflowNode", position: { x: 80,  y: 180 }, data: { blockType: "brand_context",   label: BLOCK_DEFS.brand_context.label,   config: { brand_name: agent.brand_name } } },
    { id: "analysis_prompt", type: "workflowNode", position: { x: 360, y: 180 }, data: { blockType: "analysis_prompt", label: BLOCK_DEFS.analysis_prompt.label, config: { prompt: agent.prompt } } },
    { id: "ai_models",       type: "workflowNode", position: { x: 640, y: 180 }, data: { blockType: "ai_models",       label: BLOCK_DEFS.ai_models.label,       config: { models: agent.selected_models } } },
  ];
  const edges: Edge[] = [
    { id: "e-bc-ap", source: "brand_context", target: "analysis_prompt", label: "defines brand for", type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed }, style: { strokeWidth: 2 } },
    { id: "e-ap-am", source: "analysis_prompt", target: "ai_models",     label: "runs on",           type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed }, style: { strokeWidth: 2 } },
  ];

  let oy = 80;
  if (agent.run_options.perception_score) {
    nodes.push({ id: "perception_score", type: "workflowNode", position: { x: 920, y: oy }, data: { blockType: "perception_score", label: BLOCK_DEFS.perception_score.label, config: {} } });
    edges.push({ id: `e-am-ps`, source: "ai_models", target: "perception_score", label: "generates", type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed }, style: { strokeWidth: 2 } });
    oy += 180;
  }
  if (agent.run_options.enable_download) {
    nodes.push({ id: "download_report", type: "workflowNode", position: { x: 920, y: oy }, data: { blockType: "download_report", label: BLOCK_DEFS.download_report.label, config: {} } });
    edges.push({ id: `e-am-dr`, source: "ai_models", target: "download_report", label: "exports to", type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed }, style: { strokeWidth: 2 } });
    oy += 180;
  }
  if (agent.run_options.enable_email) {
    nodes.push({ id: "send_email", type: "workflowNode", position: { x: 920, y: oy }, data: { blockType: "send_email", label: BLOCK_DEFS.send_email.label, config: { email: agent.run_options.email_address || "" } } });
    edges.push({ id: `e-am-se`, source: "ai_models", target: "send_email", label: "sends via", type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed }, style: { strokeWidth: 2 } });
  }
  return { nodes, edges };
}

function flowToAgent(nodes: Node<NodeData>[], name: string, description: string, existingTags: string[] = []): AgentBody {
  const get = (type: BlockType) => nodes.find(n => n.data.blockType === type)?.data.config || {};
  const bb = get("brand_context");
  const pb = get("analysis_prompt");
  const mb = get("ai_models");
  const eb = get("send_email");
  return {
    name: name.trim(),
    description: description.trim(),
    brand_name:      bb.brand_name || "",
    prompt:          pb.prompt || "",
    selected_models: mb.models || [],
    run_options: {
      perception_score:  nodes.some(n => n.data.blockType === "perception_score"),
      detailed_analysis: false,
      enable_download:   nodes.some(n => n.data.blockType === "download_report"),
      enable_email:      nodes.some(n => n.data.blockType === "send_email"),
      email_address:     eb.email || undefined,
    },
    tags: existingTags,
  };
}

function makeDefaultNodes(): Node<NodeData>[] {
  return [
    { id: "brand_context",   type: "workflowNode", position: { x: 80,  y: 180 }, data: { blockType: "brand_context",   label: BLOCK_DEFS.brand_context.label,   config: { brand_name: localStorage.getItem("bm_brand") || "" } } },
    { id: "analysis_prompt", type: "workflowNode", position: { x: 360, y: 180 }, data: { blockType: "analysis_prompt", label: BLOCK_DEFS.analysis_prompt.label, config: { prompt: "" } } },
    { id: "ai_models",       type: "workflowNode", position: { x: 640, y: 180 }, data: { blockType: "ai_models",       label: BLOCK_DEFS.ai_models.label,       config: { models: [...MEASUREMENT_DEFAULT_MODELS] } } },
  ];
}

function makeDefaultEdges(): Edge[] {
  return [
    { id: "e-bc-ap", source: "brand_context", target: "analysis_prompt", label: "defines brand for", type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed }, style: { strokeWidth: 2 } },
    { id: "e-ap-am", source: "analysis_prompt", target: "ai_models",     label: "runs on",           type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed }, style: { strokeWidth: 2 } },
  ];
}

// ─── Custom Workflow Node ─────────────────────────────────────────────────────

function WorkflowNode({ data, selected }: NodeProps<NodeData>) {
  const def = BLOCK_DEFS[data.blockType];
  const Icon = def.icon;
  const summary = getNodeSummary(data);

  /* ⚠ FIXED width and height, from --ctl-w-node and --ctl-h-node.
     That is the geometry rule the handoff states: derive the handle position
     and the edge endpoints from ONE constant. A content-height card with
     handles at top:50% desyncs from the path coordinates, and the handoff
     reports a 6 to 14px mismatch shipped exactly that way. ReactFlow places
     the handles; the fixed box is what makes the two agree. */
  return (
    <div
      className="upm-wf-node"
      data-selected={selected}
      style={selected ? { borderColor: def.accent, boxShadow: `0 0 0 var(--bd-w-active) ${def.line}` } : undefined}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: def.accent, borderColor: "var(--s-raised)" }}
      />

      <div className="upm-wf-node__accent" style={{ background: def.accent }} />

      <div className="upm-wf-node__inner">
        <div className="upm-wf-node__head">
          <span className="upm-wf-node__chip" style={{ background: def.tint, color: def.ink }}>
            <Icon className="k-i k-i--sm" aria-hidden="true" />
          </span>
          <span className="upm-wf-node__label">{blockLabel(data.blockType)}</span>
        </div>

        {summary && <p className="upm-wf-node__summary">{summary}</p>}

        <span
          className="upm-wf-node__cat"
          style={{ background: def.tint, color: def.ink, borderColor: def.line }}
        >
          {def.category}
        </span>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        style={{ background: def.accent, borderColor: "var(--s-raised)" }}
      />
    </div>
  );
}

// ─── Block Palette Item ───────────────────────────────────────────────────────

function PaletteItem({ type, disabled }: { type: BlockType; disabled: boolean }) {
  const def = BLOCK_DEFS[type];
  const Icon = def.icon;

  const onDragStart = (e: React.DragEvent) => {
    if (disabled) { e.preventDefault(); return; }
    e.dataTransfer.setData("palette-block-type", type);
    e.dataTransfer.effectAllowed = "move";
  };

  /* "Already on canvas" is an opacity and a check glyph, not a badge. The rail
     is 220px: a 26px chip plus a wide text badge leaves the name three words a
     line, which is the trap the handoff names. */
  return (
    <div
      draggable={!disabled}
      onDragStart={onDragStart}
      title={disabled ? "Already on canvas" : `Drag to add ${blockLabel(type)}`}
      className="upm-wf__paletteitem"
      data-used={disabled}
      style={disabled ? undefined : { borderColor: def.line, background: def.tint }}
    >
      <span className="upm-wf__palettechip" style={{ background: def.tint, color: def.ink }}>
        <Icon className="k-i k-i--sm" aria-hidden="true" />
      </span>
      <span className="upm-wf__palettetext">
        <span className="upm-wf__palettename" style={{ display: "block" }}>{blockLabel(type)}</span>
        <span className="upm-wf__palettedesc">{def.desc}</span>
      </span>
      {disabled && (
        <span className="upm-wf__paletteused">
          <Check className="k-i k-i--sm" aria-hidden="true" />
        </span>
      )}
    </div>
  );
}

// ─── Right Panel Config Forms ─────────────────────────────────────────────────

function BrandConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <Label>Brand name</Label>
        <input
          value={config.brand_name || ""}
          onChange={e => onChange({ ...config, brand_name: e.target.value })}
          placeholder="e.g. Salesforce"
          style={inputSt}
        />
        <p style={hintSt}>Pre-filled from your active analysis. Can be overridden per run.</p>
      </div>
    </div>
  );
}

function PromptConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const brand = config.brand_name || "your brand";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Label>Quick starters</Label>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {PROMPT_STARTERS.map(s => (
          <button
            key={s}
            onClick={() => onChange({ ...config, prompt: s.replace("{brand}", brand) })}
            style={{ textAlign: "left", padding: "6px 10px", borderRadius: 7, fontSize: 11, backgroundColor: "var(--a-brand-sub)", color: "var(--a-brand-t)", border: "1px solid var(--a-brand-line)", cursor: "pointer", lineHeight: 1.4 }}
          >
            <Wand2 style={{ width: 9, height: 9, display: "inline", marginRight: 5 }} />
            {s}
          </button>
        ))}
      </div>
      <Label>Prompt</Label>
      <textarea
        value={config.prompt || ""}
        onChange={e => onChange({ ...config, prompt: e.target.value })}
        rows={7}
        placeholder="Write your analysis prompt here…"
        style={{ ...inputSt, resize: "none" as const, lineHeight: 1.6 }}
      />
    </div>
  );
}

function ModelsConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const selected: string[] = config.models || [];
  const toggle = (id: string) => {
    const next = selected.includes(id) ? selected.filter(m => m !== id) : [...selected, id];
    onChange({ ...config, models: next });
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Label>Select models</Label>
      {MODEL_OPTIONS.map(m => {
        const active = selected.includes(m.id);
        return (
          <button
            key={m.id}
            onClick={() => toggle(m.id)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, border: `1px solid ${active ? "var(--a-info)" : "var(--bd-default)"}`, backgroundColor: active ? "var(--a-info-sub)" : "var(--s-subtle)", cursor: "pointer" }}
          >
            <div style={{ width: 16, height: 16, borderRadius: 4, backgroundColor: active ? "var(--a-info)" : "transparent", border: `2px solid ${active ? "var(--a-info)" : "var(--bd-strong)"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {active && <span style={{ color: "white", fontSize: 9, fontWeight: 700 }}>✓</span>}
            </div>
            <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? "var(--a-info-t)" : "var(--tx-strong)" }}>{m.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function EmailConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  return (
    <div>
      <Label>Email address</Label>
      <input value={config.email || ""} onChange={e => onChange({ ...config, email: e.target.value })} type="email" placeholder="results@yourcompany.com" style={inputSt} />
      <p style={hintSt}>Results will be emailed here after each run.</p>
    </div>
  );
}

function StaticConfig({ desc }: { desc: string }) {
  return <p style={{ fontSize: 13, color: "var(--tx-muted)", fontStyle: "italic", lineHeight: 1.6 }}>{desc}</p>;
}

function NodeConfigPanel({ node, onChange, onDelete }: { node: Node<NodeData>; onChange: (c: any) => void; onDelete: () => void }) {
  const def = BLOCK_DEFS[node.data.blockType];
  const Icon = def.icon;
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--bd-default)", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <span className="upm-wf__palettechip" style={{ background: def.tint, color: def.ink }}>
          <Icon className="k-i k-i--sm" aria-hidden="true" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--tx-strong)" }}>{blockLabel(node.data.blockType)}</div>
          <div style={{ fontSize: 11, color: "var(--tx-muted)" }}>{def.desc}</div>
        </div>
        <button
          onClick={onDelete}
          title="Remove node"
          style={{ padding: 6, borderRadius: 6, border: "none", backgroundColor: "transparent", cursor: "pointer", color: "var(--a-gap-t)" }}
        >
          <Trash2 style={{ width: 14, height: 14 }} />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
        {node.data.blockType === "brand_context"    && <BrandConfig    config={node.data.config} onChange={onChange} />}
        {node.data.blockType === "analysis_prompt"  && <PromptConfig   config={node.data.config} onChange={onChange} />}
        {node.data.blockType === "ai_models"         && <ModelsConfig   config={node.data.config} onChange={onChange} />}
        {node.data.blockType === "send_email"        && <EmailConfig    config={node.data.config} onChange={onChange} />}
        {node.data.blockType === "perception_score"  && <StaticConfig   desc="A 0–100 perception score will be generated from each model's response and aggregated." />}
        {node.data.blockType === "download_report"   && <StaticConfig   desc="After the run, results are available to download as a structured JSON report." />}
      </div>
    </div>
  );
}

function EdgeConfigPanel({ edge, onChange }: { edge: Edge; onChange: (label: string) => void }) {
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <Link style={{ width: 15, height: 15, color: "var(--tx-muted)" }} />
        <span style={{ fontWeight: 700, fontSize: 14 }}>Connection label</span>
      </div>
      <Label>Label text</Label>
      <input
        value={String(edge.label || "")}
        onChange={e => onChange(e.target.value)}
        placeholder="e.g. feeds into, runs on…"
        style={inputSt}
      />
      <p style={hintSt}>This label appears on the connection arrow in the canvas.</p>
    </div>
  );
}

function EmptyPanel() {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--tx-faint)", textAlign: "center", padding: 24 }}>
      <MousePointerClick style={{ width: 28, height: 28, marginBottom: 10, opacity: 0.4 }} />
      <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Select a node or connection</p>
      <p style={{ fontSize: 11, lineHeight: 1.5 }}>Click any block on the canvas to configure it. Click a connection arrow to edit its label.</p>
    </div>
  );
}

// ─── Style atoms ──────────────────────────────────────────────────────────────

const inputSt: React.CSSProperties = {
  width: "100%", padding: "8px 11px", borderRadius: 8,
  border: "1px solid var(--bd-default)",
  backgroundColor: "var(--s-subtle)",
  fontSize: 13, color: "var(--tx-strong)",
  outline: "none", boxSizing: "border-box",
};
const hintSt: React.CSSProperties = { fontSize: 11, color: "var(--tx-faint)", marginTop: 5 };

function Label({ children }: { children: React.ReactNode }) {
  return <p style={{ fontSize: 11, fontWeight: 700, color: "var(--tx-faint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{children}</p>;
}

// ─── Desktop-Only Screen (shown on mobile/tablet) ─────────────────────────────

function DesktopOnlyScreen({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="flex flex-col items-center justify-center p-6"
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--s-canvas)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <div className="w-full max-w-md text-center">
        <div
          className="mx-auto mb-5 flex items-center justify-center rounded-2xl"
          style={{
            width: 72,
            height: 72,
            backgroundColor: "var(--a-brand-sub)",
            border: "1px solid var(--a-brand-line)",
          }}
        >
          <Monitor style={{ width: 34, height: 34, color: "var(--a-brand-t)" }} />
        </div>

        <h2
          style={{
            fontSize: "clamp(20px, 5vw, 24px)",
            fontWeight: 700,
            marginBottom: 10,
            color: "var(--tx-strong)",
            lineHeight: 1.3,
          }}
        >
          Open on desktop to edit workflows
        </h2>
        <p
          style={{
            fontSize: 14,
            color: "var(--tx-muted)",
            lineHeight: 1.6,
            marginBottom: 24,
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          The visual workflow editor needs a wider screen than this device offers. Open
          Upmode on a laptop or desktop (≥{MIN_BUILDER_WIDTH}px wide) to drag, connect,
          and configure workflow blocks.
        </p>

        <div
          className="p-4 rounded-xl text-left mb-5"
          style={{
            backgroundColor: "var(--s-subtle)",
            border: "1px solid var(--bd-default)",
          }}
        >
          <p
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "var(--tx-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 8,
            }}
          >
            On mobile you can still
          </p>
          <ul
            style={{
              fontSize: 13,
              color: "var(--tx-strong)",
              lineHeight: 1.7,
              listStyle: "disc",
              paddingLeft: 20,
              margin: 0,
            }}
          >
            <li>Browse your workflow list</li>
            <li>Run existing workflows</li>
            <li>View run history and results</li>
          </ul>
        </div>

        <button
          onClick={onClose}
          className="w-full flex items-center justify-center gap-2"
          style={{
            padding: "12px 20px",
            borderRadius: 10,
            border: "1px solid var(--bd-default)",
            backgroundColor: "var(--s-subtle)",
            color: "var(--tx-strong)",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <ArrowLeft style={{ width: 15, height: 15 }} />
          Back to Workflows
        </button>
      </div>
    </div>
  );
}

// ─── Inner builder (needs ReactFlowProvider context) ──────────────────────────

interface AgentBuilderInnerProps {
  agent?: Agent | null;
  onClose: () => void;
  onSaved: (agent: Agent) => void;
}

function AgentBuilderInner({ agent, onClose, onSaved }: AgentBuilderInnerProps) {
  const isEdit = !!agent;
  const rfInstance = useReactFlow();

  const [name,        setName]        = useState(agent?.name ?? "");
  const [description, setDescription] = useState(agent?.description ?? "");
  const [saving,      setSaving]      = useState(false);
  const [errors,      setErrors]      = useState<string[]>([]);

  const initFlow = agent ? agentToFlow(agent) : { nodes: makeDefaultNodes(), edges: makeDefaultEdges() };
  const [nodes, setNodes, onNodesChange] = useNodesState<NodeData>(initFlow.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initFlow.edges);

  const nodeTypes = useMemo(() => ({ workflowNode: WorkflowNode }), []);

  const selectedNode = nodes.find(n => n.selected) ?? null;
  const selectedEdge = edges.find(e => e.selected) ?? null;
  const usedBlockTypes = new Set(nodes.map(n => n.data.blockType));

  const onConnect = useCallback((connection: Connection) => {
    const sourceNode = nodes.find(n => n.id === connection.source);
    const targetNode = nodes.find(n => n.id === connection.target);
    const label = (sourceNode && targetNode)
      ? getEdgeLabel(sourceNode.data.blockType, targetNode.data.blockType)
      : "connects to";

    setEdges(eds => addEdge({
      ...connection,
      label,
      type: "smoothstep",
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { strokeWidth: 2 },
    }, eds));
  }, [nodes, setEdges]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const blockType = e.dataTransfer.getData("palette-block-type") as BlockType;
    if (!blockType || usedBlockTypes.has(blockType)) return;

    const position = rfInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const newNode: Node<NodeData> = {
      id: `${blockType}-${Date.now()}`,
      type: "workflowNode",
      position,
      data: {
        blockType,
        label: BLOCK_DEFS[blockType].label,
        config:
          blockType === "brand_context" ? { brand_name: localStorage.getItem("bm_brand") || "" } :
          blockType === "ai_models"     ? { models: [...MEASUREMENT_DEFAULT_MODELS] } : {},
      },
    };
    setNodes(nds => [...nds, newNode]);
  }, [rfInstance, setNodes, usedBlockTypes]);

  const updateNodeConfig = useCallback((nodeId: string, config: any) => {
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, config } } : n));
  }, [setNodes]);

  const updateEdgeLabel = useCallback((edgeId: string, label: string) => {
    setEdges(eds => eds.map(e => e.id === edgeId ? { ...e, label } : e));
  }, [setEdges]);

  const deleteNode = useCallback((nodeId: string) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
  }, [setNodes, setEdges]);

  const handleSave = async () => {
    const errs: string[] = [];
    if (!name.trim()) errs.push("Workflow name is required");
    const bb = nodes.find(n => n.data.blockType === "brand_context");
    if (!bb?.data.config.brand_name?.trim()) errs.push("Brand context: a brand name is required");
    const pb = nodes.find(n => n.data.blockType === "analysis_prompt");
    if (!pb?.data.config.prompt?.trim()) errs.push("Analysis prompt: prompt text is required");
    const mb = nodes.find(n => n.data.blockType === "ai_models");
    if (!mb?.data.config.models?.length) errs.push("AI Models: select at least one model");
    if (errs.length) { setErrors(errs); return; }
    setErrors([]);
    setSaving(true);
    try {
      const body = flowToAgent(nodes, name, description, agent?.tags);
      if (isEdit && agent) {
        await agentsApi.update(agent.id, body);
        onSaved({ ...agent, ...body, updated_at: new Date().toISOString() });
      } else {
        const result = await agentsApi.create(body);
        onSaved({ ...body, id: result.id, username: "", is_active: true, created_at: result.created_at, updated_at: result.created_at } as Agent);
      }
    } catch (e: any) {
      toast.error(e.message || "Failed to save workflow");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", backgroundColor: "var(--s-canvas)", fontFamily: "var(--font-sans)" }}>

      {/* ── Top Bar ─────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", borderBottom: "1px solid var(--bd-default)", flexShrink: 0, backgroundColor: "var(--s-canvas)", zIndex: 10 }}>
        <button onClick={onClose} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 8, border: "1px solid var(--bd-default)", backgroundColor: "var(--s-subtle)", cursor: "pointer", color: "var(--tx-strong)", fontSize: 13, fontWeight: 500 }}>
          <ArrowLeft style={{ width: 14, height: 14 }} />
          Back
        </button>

        <div style={{ width: 1, height: 28, backgroundColor: "var(--bd-default)" }} />

        <div style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: "var(--a-brand-sub)", border: "1px solid var(--a-brand-line)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Bot style={{ width: 15, height: 15, color: "var(--a-brand-t)" }} />
        </div>

        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Workflow name *"
          style={{ ...inputSt, width: 220, fontWeight: 600, fontSize: 14, flexShrink: 0 }}
        />
        <input
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Description (optional)"
          style={{ ...inputSt, flex: 1, fontSize: 13, minWidth: 0 }}
        />

        <Button
          icon={Save}
          onClick={handleSave}
          loading={saving}
          loadingLabel="Saving"
        >
          {isEdit ? "Save changes" : "Create workflow"}
        </Button>
      </div>

      {errors.length > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 20px", backgroundColor: "var(--a-gap-sub)", borderBottom: "1px solid var(--a-gap-line)", flexShrink: 0 }}>
          <AlertCircle style={{ width: 15, height: 15, color: "var(--a-gap-t)", flexShrink: 0, marginTop: 1 }} />
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {errors.map((e, i) => <span key={i} style={{ fontSize: 12, color: "var(--a-gap-t)" }}>{e}</span>)}
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "220px 1fr 280px", overflow: "hidden" }}>

        <div style={{ borderRight: "1px solid var(--bd-default)", backgroundColor: "var(--muted-10, var(--s-subtle))", overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 18 }}>
          <div>
            <p style={{ fontSize: 10, fontWeight: 800, color: "var(--tx-faint)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8, padding: "0 2px" }}>Setup blocks</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {SETUP_TYPES.map(t => <PaletteItem key={t} type={t} disabled={usedBlockTypes.has(t)} />)}
            </div>
          </div>
          <div>
            <p style={{ fontSize: 10, fontWeight: 800, color: "var(--tx-faint)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8, padding: "0 2px" }}>Output blocks</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {OUTPUT_TYPES.map(t => <PaletteItem key={t} type={t} disabled={usedBlockTypes.has(t)} />)}
            </div>
          </div>

          <div style={{ marginTop: "auto", padding: "10px 11px", borderRadius: 8, backgroundColor: "var(--s-hover)", border: "1px solid var(--bd-default)" }}>
            <p style={{ fontSize: 11, color: "var(--tx-muted)", lineHeight: 1.5, margin: 0 }}>
              <strong>Drag</strong> blocks onto the canvas. <strong>Click</strong> a block or arrow to configure it. <strong>Connect</strong> blocks by dragging from a node's right handle to another's left handle.
            </p>
          </div>
        </div>

        <div style={{ position: "relative", backgroundColor: "var(--s-canvas)" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDragOver={onDragOver}
            onDrop={onDrop}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            deleteKeyCode={["Backspace", "Delete"]}
            defaultEdgeOptions={{
              type: "smoothstep",
              markerEnd: { type: MarkerType.ArrowClosed },
              style: { strokeWidth: 2 },
            }}
            style={{ backgroundColor: "var(--s-canvas)" }}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={20}
              size={1.2}
              color="var(--bd-default)"
            />
            <Controls
              style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 8 }}
              showInteractive={false}
            />
          </ReactFlow>
        </div>

        <div style={{ borderLeft: "1px solid var(--bd-default)", display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "var(--s-canvas)" }}>
          <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--bd-default)", flexShrink: 0 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: "var(--tx-faint)", textTransform: "uppercase", letterSpacing: "0.07em", margin: 0 }}>
              {selectedNode ? "Configure block" : selectedEdge ? "Edit connection" : "Properties"}
            </p>
          </div>

          <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {selectedNode ? (
              <NodeConfigPanel
                node={selectedNode}
                onChange={cfg => updateNodeConfig(selectedNode.id, cfg)}
                onDelete={() => deleteNode(selectedNode.id)}
              />
            ) : selectedEdge ? (
              <EdgeConfigPanel
                edge={selectedEdge}
                onChange={label => updateEdgeLabel(selectedEdge.id, label)}
              />
            ) : (
              <EmptyPanel />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Public Export (wraps with ReactFlowProvider + viewport gate) ─────────────

export interface AgentBuilderProps {
  agent?: Agent | null;
  onClose: () => void;
  onSaved: (agent: Agent) => void;
}

export function AgentBuilder(props: AgentBuilderProps) {
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= MIN_BUILDER_WIDTH;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const check = () => setIsDesktop(window.innerWidth >= MIN_BUILDER_WIDTH);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  if (!isDesktop) {
    return <DesktopOnlyScreen onClose={props.onClose} />;
  }

  return (
    <ReactFlowProvider>
      <AgentBuilderInner {...props} />
    </ReactFlowProvider>
  );
}